import { type HTMLAttributes } from 'react';

import { cn } from '../../lib/cn';

/**
 * docs/02-DESIGN-SYSTEM.md §4.1 — "Circular. Image, or initials on
 * `--navy-600` with gold text."
 *
 * Initials are computed by the caller, not here: `StaffListResource` already
 * returns `photo.initials` (docs/04-API-SPEC.md §2), and Georgian name
 * splitting is not something the frontend should be guessing at.
 *
 * Gold on navy-600 is 4.6:1, which passes AA — this is one of the placements
 * §1.5 explicitly permits gold in.
 */
export type AvatarSize = 'sm' | 'md' | 'lg' | 'xl';

const SIZES: Record<AvatarSize, string> = {
  sm: 'h-8 w-8 text-caption',
  md: 'h-11 w-11 text-body-sm',
  lg: 'h-16 w-16 text-h4',
  xl: 'h-24 w-24 text-h3',
};

/** Intrinsic pixel size, so every <img> carries width/height and cannot shift. */
const PIXELS: Record<AvatarSize, number> = { sm: 32, md: 44, lg: 64, xl: 96 };

export interface AvatarProps extends Omit<HTMLAttributes<HTMLSpanElement>, 'children'> {
  src?: string | null;
  /**
   * Describes the image. For a staff photo this is the person's name; §7.5
   * requires meaningful alt text from the media library.
   */
  alt?: string;
  /** Fallback when there is no photo, e.g. "მბ". Comes from the API. */
  initials?: string;
  size?: AvatarSize;
}

export function Avatar({ src, alt = '', initials, size = 'md', className, ...props }: AvatarProps) {
  const pixels = PIXELS[size];

  return (
    <span
      className={cn(
        'inline-flex shrink-0 select-none items-center justify-center overflow-hidden rounded-full',
        'bg-navy-600 font-bold text-gold-500',
        SIZES[size],
        className,
      )}
      {...props}
    >
      {src ? (
        <img
          src={src}
          alt={alt}
          width={pixels}
          height={pixels}
          loading="lazy"
          decoding="async"
          className="h-full w-full object-cover"
        />
      ) : (
        // With no photo and no initials there is nothing to announce, so the
        // circle is decoration rather than an empty labelled element.
        <span aria-hidden={initials ? undefined : 'true'}>{initials}</span>
      )}
    </span>
  );
}
